/**
 * Wordle Statistics Service
 *
 * Records game results, tracks streaks, guess distribution,
 * win rate, and average guesses.
 */

import { WordleStats, WordleGameResult } from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class WordleStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'wordle_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): WordleStats {
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      winPercentage: 0,
      currentStreak: 0,
      maxStreak: 0,
      guessDistribution: [0, 0, 0, 0, 0, 0],
      averageGuesses: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): WordleStats {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as WordleStats) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get wordle statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. Only daily games affect stats. */
  recordGameResult(result: WordleGameResult, userId?: string): boolean {
    try {
      // Only daily mode affects stats
      if (result.mode !== 'daily') return true;

      const stats = this.getStatistics(userId);

      stats.gamesPlayed++;
      stats.lastPlayedDate = result.date;

      if (result.puzzleNumber !== undefined) {
        stats.lastCompletedPuzzle = result.puzzleNumber;
      }

      if (result.won) {
        stats.gamesWon++;
        stats.currentStreak++;
        stats.maxStreak = Math.max(stats.maxStreak, stats.currentStreak);
        // guessDistribution[0] = won in 1 guess, etc.
        const guessIndex = result.guessCount - 1;
        if (guessIndex >= 0 && guessIndex < 6) {
          stats.guessDistribution[guessIndex]++;
        }
      } else {
        stats.currentStreak = 0;
      }

      // Update win percentage
      stats.winPercentage = stats.gamesPlayed > 0
        ? Math.round((stats.gamesWon / stats.gamesPlayed) * 100)
        : 0;

      // Update average guesses (for won games only)
      const totalGuesses = stats.guessDistribution.reduce(
        (sum, count, idx) => sum + count * (idx + 1), 0
      );
      stats.averageGuesses = stats.gamesWon > 0
        ? Math.round((totalGuesses / stats.gamesWon) * 10) / 10
        : 0;

      // Record in stats manager too
      this.statsManager.recordGameResult({
        gameId: `wordle-${result.date}`,
        winner: result.won ? 'player' : undefined,
        isDraw: false,
        mode: result.mode,
        duration: result.timeSeconds,
        completedAt: new Date(),
      });

      this.saveStatistics(stats, userId);
      return true;
    } catch (error) {
      console.error('Failed to record wordle game result:', error);
      return false;
    }
  }

  /** Reset statistics (for testing or user request) */
  resetStatistics(userId?: string): void {
    const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
    localStorage.removeItem(key);
  }

  private saveStatistics(stats: WordleStats, userId?: string): void {
    const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
    localStorage.setItem(key, JSON.stringify(stats));
  }
}
